import { useState } from "react";
import { Link, Outlet, useLocation, useRouteLoaderData, useSearchParams } from "react-router-dom";

import ServerFooter from "../common/ServerFooter";
import UserInfo from "../common/userInfo";
import { useAuthStore, User } from "../main/authStore";

import { RentalRecord } from "./types";

export default function RentalStatusList() {
    const { rentals, total }: { rentals: RentalRecord[]; total: number } = useRouteLoaderData("Rentals")!;
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const user = useAuthStore(state => state.user) as User;
    const [search, setSearch] = useState("");
    const [startFrom, setStartFrom] = useState(searchParams.get("rentalStartDateFrom") ?? "");
    const [startTo, setStartTo] = useState(searchParams.get("rentalStartDateTo") ?? "");
    const [status, setStatus] = useState(searchParams.get("status") ?? "all");
    const filtered = rentals.filter(rental => JSON.stringify(rental).toLowerCase().includes(search.toLowerCase()));

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-center flex-wrap gap-3">
                <label htmlFor = "rental-search" className = "mb-0">Search</label>
                <input
                    id = "rental-search"
                    className = "form-control inline w-auto"
                    value = {search}
                    onChange = {e => setSearch(e.currentTarget.value)}
                />
                <label htmlFor = "rental-status" className = "mb-0">Status</label>
                <select
                    id = "rental-status"
                    className = "form-control inline w-auto"
                    value = {status}
                    onChange = {e => setStatus(e.currentTarget.value)}
                >
                    <option value = "all">All</option>
                    <option value = "rented">Rented</option>
                    <option value = "returned">Returned</option>
                </select>
                <label htmlFor = "rentalStartDateFrom" className = "mb-0">Start</label>
                <input
                    type = "date"
                    id = "rentalStartDateFrom"
                    className = "form-control inline w-auto"
                    value = {startFrom}
                    max = {startTo || undefined}
                    onChange = {e => setStartFrom(e.currentTarget.value)}
                />
                <label htmlFor = "rentalStartDateTo" className = "mb-0">End</label>
                <input
                    type = "date"
                    id = "rentalStartDateTo"
                    className = "form-control inline w-auto"
                    value = {startTo}
                    min = {startFrom || undefined}
                    onChange = {e => setStartTo(e.currentTarget.value)}
                />
                <button
                    type = "button"
                    className = "btn btn-outline-primary"
                    onClick = {() => {
                        if (!startFrom || !startTo) {
                            window.alert("Both Start and End dates are required.");
                            return;
                        }
                        if (startFrom > startTo) {
                            window.alert("Start date cannot be after end date.");
                            return;
                        }
                        const next = new URLSearchParams(searchParams);
                        next.set("status", status);
                        next.set("page", "1");
                        next.set("rentalStartDateFrom", startFrom);
                        next.set("rentalStartDateTo", startTo);
                        setSearchParams(next);
                    }}
                >
                    Load
                </button>
                {user.projects !== "no" ? <Link className = "btn btn-outline-primary" to = "Add">Add Rental</Link> : null}
            </nav>
            {!filtered.length ? (
                <div className = "cssLoading">Empty</div>
            ) : (
                <div className = "table-responsive">
                    <table className = "table">
                        <thead className = "noTableTopBorder">
                            <tr>
                                <th>Equipment</th>
                                <th>Project</th>
                                <th>Technician</th>
                                <th>Department</th>
                                <th>Start Date</th>
                                <th>Expire Date</th>
                                <th>Status</th>
                                <th>Returned By</th>
                                <th>Return Date</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(rental => (
                                <tr key = {rental.id}>
                                    <td>{rental.equipmentName}</td>
                                    <td>{rental.projectId ? <Link to = {`/Projects/${rental.projectId}`}>{rental.projectName}</Link> : rental.projectName}</td>
                                    <td><UserInfo name = {rental.renterName} id = {rental.renterId} /></td>
                                    <td>{rental.department}</td>
                                    <td>{rental.rentalStartDate}</td>
                                    <td>{rental.rentalExpireDate}</td>
                                    <td><span className = {`badge text-bg-${rental.status === "returned" ? "success" : "primary"}`}>{rental.status}</span></td>
                                    <td>{rental.returnedById ? <UserInfo name = {rental.returnedByName} id = {rental.returnedById} /> : null}</td>
                                    <td>{rental.rentalReturnDate}</td>
                                    <td><Link className = "btn btn-outline-primary" to = {rental.id + location.search}>Detail</Link></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {total ? <ServerFooter length = {total} /> : null}
            <Outlet />
        </div>
    );
}
